/**
 * LA SALUD DE LOS CLIENTES: QUIEN ESTA AL DIA Y QUIEN NO, Y POR QUE (SALUD-CLIENTES-T1, parte P; ASSIGN de Ares #24364).
 *
 * DE DONDE SALE. A calcula la foto diaria (nexo_private.salud_clientes_diaria) con los umbrales que fijo Ares (#24363)
 * y la sirve en GET /api/v1/salud/tablero, bloque "clientes" (forma propuesta por P en #24401):
 *   clientes: { fecha, resumen: [{ dimension, alDia, total, sinFuente }],
 *               negocios: [{ businessId, nombre, dimensiones: [{ dimension, valor, unidad, alDia, fuente, explica }] }] }
 *
 * LAS REGLAS (de Ares; el panel no las recalcula):
 *   - universo: negocios activos sin demo ni sin_cartera (lo filtra la API)
 *   - alDia lo trae la API; null = sin fuente (capacitacion hoy: pendiente PASE-COLAB-T1), NUNCA cero ni "mal"
 *   - el negocio que mas debe va primero: es lo que Josse tiene que mirar
 *   - si la API todavia no trae el bloque, se dice; el resto de la Sala sigue
 *
 * Sin DOM: lo prueba scripts/check-salud-clientes.mjs.
 */

const pick = (o, ...claves) => {
  for (const k of claves) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return null;
};
const numero = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());
const booleano = (v) => (v === true || v === 'true' || v === 't' ? true : v === false || v === 'false' || v === 'f' ? false : null);

/** Las seis dimensiones, en el orden de la pantalla, con su nombre en idioma de negocio. */
export const DIMENSIONES = [
  { clave: 'pago', titulo: 'Pago', pregunta: 'Paga a tiempo' },
  { clave: 'version', titulo: 'Programa', pregunta: 'Tiene el programa al dia' },
  { clave: 'datos', titulo: 'Ventas', pregunta: 'Sube sus ventas' },
  { clave: 'alertas', titulo: 'Errores', pregunta: 'No tiene errores sin revisar' },
  { clave: 'capacitacion', titulo: 'Capacitacion', pregunta: 'Su equipo se capacita' },
  { clave: 'asesoria', titulo: 'Asesoria', pregunta: 'Lo acompanamos' }
];
const CLAVES = DIMENSIONES.map((d) => d.clave);

/** El estado de una celda: bien, mal o sin fuente (null). */
export function estadoDe(alDia) {
  return alDia === true ? 'bien' : alDia === false ? 'mal' : 'sin-fuente';
}

/** La frase corta de una celda, por dimension y valor, en idioma de negocio. */
export function fraseDe(dim) {
  const v = dim.valor;
  if (dim.alDia === null && v === null) return dim.explica || 'sin fuente todavia';
  switch (dim.dimension) {
    case 'pago': return v === null ? (dim.explica || 'sin suscripcion cargada') : v === 0 ? 'al dia' : `debe ${v} ${v === 1 ? 'mes' : 'meses'}`;
    case 'version': return v === null ? (dim.explica || 'sin PC visto en 30 dias') : v === 0 ? 'ultima version' : `${v} ${v === 1 ? 'version' : 'versiones'} atras`;
    case 'datos': return v === null ? (dim.explica || 'nunca ha subido ventas') : v === 0 ? 'subio hoy' : `${v} ${v === 1 ? 'dia' : 'dias'} sin subir`;
    case 'alertas': return v === null ? (dim.explica || 'sin dato') : v === 0 ? 'sin errores' : `${v} ${v === 1 ? 'error' : 'errores'} sin revisar`;
    case 'asesoria': return v === null ? (dim.explica || 'nunca le ha hablado Argemiro') : `hace ${v} ${v === 1 ? 'dia' : 'dias'}`;
    default: return v === null ? (dim.explica || 'sin dato') : String(v);
  }
}

function leerDimension(x) {
  return {
    dimension: texto(pick(x, 'dimension', 'Dimension', 'clave')).toLowerCase(),
    valor: numero(pick(x, 'valor', 'Valor')),
    unidad: texto(pick(x, 'unidad', 'Unidad')),
    alDia: booleano(pick(x, 'alDia', 'al_dia', 'AlDia')),
    fuente: texto(pick(x, 'fuente', 'Fuente')),
    explica: texto(pick(x, 'explica', 'Explica', 'detalle'))
  };
}

/**
 * El bloque "clientes" del tablero, listo para pintar; null si la API todavia no lo trae.
 * @returns {null | {fecha: string, resumen: {dimension: string, titulo: string, alDia: number|null, total: number|null, sinFuente: number}[],
 *   negocios: {id: string, nombre: string, deben: number, sinFuente: number, celdas: object}[]}}
 */
export function leerClientes(tablero) {
  const c = pick(tablero, 'clientes', 'Clientes');
  if (!c || typeof c !== 'object') return null;
  const negociosCrudos = Array.isArray(pick(c, 'negocios', 'Negocios')) ? pick(c, 'negocios', 'Negocios') : [];
  const negocios = negociosCrudos.map((n) => {
    const dims = (Array.isArray(pick(n, 'dimensiones', 'Dimensiones')) ? pick(n, 'dimensiones', 'Dimensiones') : []).map(leerDimension);
    const celdas = {};
    for (const clave of CLAVES) {
      const d = dims.find((x) => x.dimension === clave) || { dimension: clave, valor: null, unidad: '', alDia: null, fuente: '', explica: 'la API no trajo esta dimension' };
      celdas[clave] = { ...d, estado: estadoDe(d.alDia), frase: fraseDe(d) };
    }
    const deben = CLAVES.filter((k) => celdas[k].estado === 'mal').length;
    const sinFuente = CLAVES.filter((k) => celdas[k].estado === 'sin-fuente').length;
    const id = texto(pick(n, 'businessId', 'business_id', 'BusinessId'));
    return { id, nombre: texto(pick(n, 'nombre', 'Nombre')) || id, deben, sinFuente, celdas };
  }).filter((n) => n.id)
    .sort((a, b) => (b.deben - a.deben) || a.nombre.localeCompare(b.nombre, 'es'));

  const resumenCrudo = Array.isArray(pick(c, 'resumen', 'Resumen')) ? pick(c, 'resumen', 'Resumen') : [];
  const resumen = DIMENSIONES.map((d) => {
    const r = resumenCrudo.find((x) => texto(pick(x, 'dimension', 'Dimension')).toLowerCase() === d.clave);
    if (r) {
      return { dimension: d.clave, titulo: d.titulo, alDia: numero(pick(r, 'alDia', 'al_dia')), total: numero(pick(r, 'total', 'Total')), sinFuente: numero(pick(r, 'sinFuente', 'sin_fuente')) ?? 0 };
    }
    // Sin resumen de la API: se cuenta de las filas (misma regla: sin fuente no suma ni resta).
    const conDato = negocios.filter((n) => n.celdas[d.clave].estado !== 'sin-fuente');
    return { dimension: d.clave, titulo: d.titulo, alDia: conDato.length ? conDato.filter((n) => n.celdas[d.clave].estado === 'bien').length : null, total: negocios.length, sinFuente: negocios.length - conDato.length };
  });

  return { fecha: texto(pick(c, 'fecha', 'Fecha')).slice(0, 10), resumen, negocios };
}

/**
 * Toda la dimension sin fuente. OJO con la forma servida (nexo-api 466, dfec9e7): SaludClientesResumenDto.AlDia es int, no
 * nullable, asi que una dimension sin fuente llega como { alDia: 0, sinFuente: 33, total: 33 }: eso NO es "0 de 33".
 */
const todaSinFuente = (r) => r.alDia === null || r.total === null || r.total === 0 || (r.sinFuente ?? 0) >= r.total;

/** "23 de 33" o "sin fuente" para el resumen. */
export function fraccionDe(r) {
  if (todaSinFuente(r)) return 'sin fuente';
  return `${r.alDia} de ${r.total}`;
}

/** "10 sin dato" cuando parte de la casa no tiene de donde sacar el dato; '' si no aplica. */
export function sinDatoDe(r) {
  if (todaSinFuente(r) || !(r.sinFuente > 0)) return '';
  return `${r.sinFuente} sin dato`;
}

/** Estado del resumen: todo al dia (de los que tienen dato), alguno no, o sin fuente. */
export function estadoDelResumen(r) {
  if (todaSinFuente(r)) return 'sin-fuente';
  return r.alDia >= r.total - (r.sinFuente ?? 0) ? 'bien' : 'mal';
}
